/**
 * The durable subset of store state and the helpers that pick it.
 *
 * Shared by the store (browser autosave), `.resolve` project files, and the
 * validation both use when reading data back. Kept separate from
 * `appStore.ts` so `utils/projectFile.ts` can depend on it without an import
 * cycle.
 */

import type {
  SystemPoint,
  PointPair,
  TransformationSolution,
  BlunderDetectionResults,
  DataSnoopingResults,
  TransformedPoint,
  ActiveTab,
  MatchingMode,
} from '@/types';

export type DetectionMethod = 'combinatorial' | 'dataSnooping' | null;

/** Everything restored by "Undo last apply". */
export interface ApplySnapshot {
  appliedScale: number | null;
  customScale: string;
  pointPairs: PointPair[];
  solution: TransformationSolution | null;
  solutionStale: boolean;
  solutionTimestamp: number | null;
  blunderResults: BlunderDetectionResults | null;
  dataSnoopingResults: DataSnoopingResults | null;
  combinatorialStale: boolean;
  dataSnoopingStale: boolean;
  activeDetectionMethod: DetectionMethod;
}

/**
 * The durable subset of store state — what survives a reload (localStorage
 * autosave) and what a .resolve project file contains. Transient UI state
 * (help panel, selection) is deliberately excluded.
 */
export interface PersistedState {
  inputRevision: number;
  solutionRevision: number;
  solutionInputRevision: number | null;
  appliedScale: number | null;
  transformedFromRevision: number | null;
  transformedTimestamp: number | null;
  transformedStale: boolean;
  units: string;
  system1Points: SystemPoint[];
  system2Points: SystemPoint[];
  pointPairs: PointPair[];
  solution: TransformationSolution | null;
  solutionStale: boolean;
  solutionTimestamp: number | null;
  blunderResults: BlunderDetectionResults | null;
  dataSnoopingResults: DataSnoopingResults | null;
  combinatorialStale: boolean;
  dataSnoopingStale: boolean;
  activeDetectionMethod: DetectionMethod;
  transformedPoints: TransformedPoint[];
  lastApplySnapshot: ApplySnapshot | null;
  rejectionLevel: number;
  viewAllSolutions: boolean;
  runAllMaxLevel: number;
  selectedAlpha: number;
  activeTab: ActiveTab;
  customScale: string;
  pointNumberOffset: number;
  transformRange: { from: string; to: string };
  matchingMode: MatchingMode;
  detectedOffset: number | null;
}

/** Fresh-session values for every durable key. */
export const createDefaultPersistedState = (): PersistedState => ({
  inputRevision: 0,
  solutionRevision: 0,
  solutionInputRevision: null,
  appliedScale: null,
  transformedFromRevision: null,
  transformedTimestamp: null,
  transformedStale: false,
  units: 'unspecified',
  system1Points: [],
  system2Points: [],
  pointPairs: [],
  solution: null,
  solutionStale: false,
  solutionTimestamp: null,
  blunderResults: null,
  dataSnoopingResults: null,
  combinatorialStale: false,
  dataSnoopingStale: false,
  activeDetectionMethod: null,
  transformedPoints: [],
  lastApplySnapshot: null,
  rejectionLevel: 1,
  viewAllSolutions: false,
  runAllMaxLevel: 3,
  selectedAlpha: 0.001,
  activeTab: 'import',
  customScale: '',
  pointNumberOffset: 0,
  transformRange: { from: '', to: '' },
  matchingMode: 'exact',
  detectedOffset: null,
});

/**
 * Every durable key. `satisfies` rejects unknown keys; the `Record` below
 * fails to compile if `PersistedState` gains a key that is not listed.
 */
export const PERSISTED_KEYS = [
  'inputRevision',
  'solutionRevision',
  'solutionInputRevision',
  'appliedScale',
  'transformedFromRevision',
  'transformedTimestamp',
  'transformedStale',
  'units',
  'system1Points',
  'system2Points',
  'pointPairs',
  'solution',
  'solutionStale',
  'solutionTimestamp',
  'blunderResults',
  'dataSnoopingResults',
  'combinatorialStale',
  'dataSnoopingStale',
  'activeDetectionMethod',
  'transformedPoints',
  'lastApplySnapshot',
  'rejectionLevel',
  'viewAllSolutions',
  'runAllMaxLevel',
  'selectedAlpha',
  'activeTab',
  'customScale',
  'pointNumberOffset',
  'transformRange',
  'matchingMode',
  'detectedOffset',
] as const satisfies readonly (keyof PersistedState)[];

const _everyPersistedKeyListed: Record<
  Exclude<keyof PersistedState, (typeof PERSISTED_KEYS)[number]>,
  never
> = {};
void _everyPersistedKeyListed;

/** Copy the durable keys out of any state that contains them. */
export const pickPersistedState = <S extends PersistedState>(
  state: S
): PersistedState => {
  const picked: Partial<PersistedState> = {};
  const copy = <K extends keyof PersistedState>(key: K) => {
    picked[key] = state[key];
  };
  PERSISTED_KEYS.forEach(copy);
  // Complete: PERSISTED_KEYS is checked above to cover every key.
  return picked as PersistedState;
};

/**
 * Heavy, cheaply recomputable result sets are excluded from localStorage
 * autosave — a View-All combinatorial run can exceed the storage quota,
 * which would make every subsequent write throw. Explicit .resolve project
 * files still include them (no quota there).
 */
export type AutosaveState = Omit<
  PersistedState,
  'blunderResults' | 'dataSnoopingResults' | 'lastApplySnapshot'
>;

export const pickAutosaveState = <S extends PersistedState>(
  state: S
): AutosaveState => {
  const {
    blunderResults: _blunderResults,
    dataSnoopingResults: _dataSnoopingResults,
    lastApplySnapshot: _lastApplySnapshot,
    ...autosaved
  } = pickPersistedState(state);
  return autosaved;
};

/** Autosave schema version; matches the project-file data shape. */
export const AUTOSAVE_VERSION = 2;
